import {
  authenticationTimeouts,
  cliLimitSessions,
  commandTimeoutsMinutes,
  loginTimeouts,
  retryAuth,
  retryDefault,
  sessionTimeouts,
  settingTypes,
  tacacsDelay,
  tacacsRetry,
} from '@ipfabric/utils';
import { assuranceEngineMessages } from 'components/AssuranceEngine/assuranceEngineMessages';

const BGP_LIMIT_MIN = 1;
const BGP_LIMIT_MAX = 10000000;

export const tagsInputIpFieldValidation = {
  required: 'Field is required',
  invalidAddressFormat: 'Invalid address format',
  invalidCidr: "{cidr} isn't valid entry",
};

const EXCLUDE_LIST_DEFAULT = [
  '0.0.0.0/8',
  '127.0.0.0/8',
  '224.0.0.0/4',
  '240.0.0.0/4',
  'fe80::/10',
  'ff00::/8',
  '::/128',
  '::1/128',
];

export const discoverySettingsMessages = {
  en: {
    startButton: {
      settings: 'Settings',
      newSnapshot: 'New snapshot',
      playButtonTooltip: 'Start discovery',
      addDevices: 'Add devices',
      updateData: 'Refresh device data',
      btnClose: 'Close',
      btnStartDiscovery: 'Start discovery',
    },
    btnSave: 'Save',
    leaveDialog: {
      btnSave: 'Start Discovery & Leave',
      btnSaveSettings: 'Save Settings & Leave',
      btnLeave: 'Leave',
      btnCancel: 'Stay',
      header: 'Data are not saved',
      subHeader: 'Do you really want to leave form without save?',
    },
    tabs: {
      advancedCli: 'Advanced CLI',
      assuranceEngine: 'Assurance Engine',
      deviceCredentials: 'Device Credentials',
      deviceAttributes: 'Device Attributes',
      disabledDiscoveryTasks: 'Disabled Discovery Tasks',
      discovery: 'Discovery',
      discoverySeed: 'Discovery Seeds',
      siteSeparation: 'Site Separation',
      vendorsApi: 'Vendors API',
      routing: 'Routing',
      manualLinks: 'Manual Device Links',
    },
    discovery: {
      pageTitle: 'Discovery',
      whitelistBoxTitle: 'Duplicate IPs discovery',
      whitelist: 'List of subnets that allow discovery of duplicate IPs',
      whitelistDescription: (
        <div className='flex flex-wrap'>
          <span>
            For example:
            <code>10.1.1.0/24</code> <code>2001:db8::/64</code>
            .&nbsp;
          </span>
        </div>
      ),
      btnDuplicateIpSubnetsRestore: 'Restore subnets to default',
      networksBoxTitle: 'IP Scope',
      exclude: 'IP networks to exclude from discovery and analysis',
      excludeDescription: (
        <div className='flex flex-wrap'>
          <span>
            For example:
            <code>10.1.1.0/24</code> <code>2001:db8::/64</code>
            .&nbsp;
          </span>
          Following prefixes are excluded automatically:
          {EXCLUDE_LIST_DEFAULT.map((item) => (
            <code key={`exclude-${item}`} style={{ marginRight: '5px' }}>
              {item}
            </code>
          ))}
        </div>
      ),
      include: 'IP networks to include in discovery and analysis',
      includeDescription: (
        <div>
          For example:
          <code>10.1.1.0/24</code> <code>2001:db8::/64</code>. Use{' '}
          <code>0.0.0.0/0</code> <code>::/0</code> to include everything
        </div>
      ),
      btnSave: 'Save',
      btnRestore: 'Restore IP networks to default',
      scannerBoxTitle: 'Scanner settings',
      enableScanner: 'Use scanner in discovery',
      shortestMask: 'Shortest mask of the network to scan',
      dnsResolveBoxTitle: 'DNS resolve',
      discoveryDevices: 'Discovered inventory - Resolve DNS name',
      traceBoxTitle: 'Traceroute settings',
      traceDescription: (
        <div>
          For example:
          <code>10.0.0.0/8</code>
        </div>
      ),
      scope: 'Trace scope',
      protocol: 'Protocol',
      port: 'Port',
      btnTraceRestore: 'Use RFC 6890 scopes',
      subnetsPlaceholer: 'Add CIDR',
      fullBgpLimit: {
        boxTitle: 'Limit download BGP routes',
        boxTitleIconHelp: (
          <div>
            It is recommended to disable BGP route processing if you have FULL
            BGP routers in the network. BGP routes will be downloaded in
            particular routing instance, if their count is less than the
            threshold.
            <br />
            <br />
            <p>
              This feature is implemented on
              <br />
              Cisco IOS, IOS-XE, IOS-XR, NX-OS
              <br />
              Juniper Junos, Arista EOS, Huawei VRP
            </p>
          </div>
        ),
        enabled: 'Control BGP routes downloading (IPv4)',
        enabledIpv6: 'Control BGP routes downloading (IPv6)',
        threshold: 'Threshold',
      },
      taskSettings: {
        boxTitle: 'Discovery tasks settings',
        alreadyDiscovered: 'Limit discovery to already discovered devices',
        sourceOfTasks: 'What information is used to identify neighbors',
        sourceOfTasksOptions: {
          aciEndpoint: 'ACI endpoints',
          xdp: 'xDP (neighbors)',
          arp: 'ARP',
          routes: 'Routing table',
          trace: 'Trace',
          ndpv6: 'NDPv6',
        },
      },
      validation: {
        noEmpty: "The inclusion list can't be empty",
        required: 'Required',
        requiredScope: "Trace scope list can't be empty",
        invalidIp: "Value {ip} isn't valid CIDR range",
        mask: 'Please provide valid mask. It must be number from 16 to 32',
        range: 'Port has to be 1-65535',
        selectType: 'Please select type of site calculation',
        regularRequired: 'Please provide Regular expression',
        invalidRegular: 'Invalid Regular Expression',
        noSlashed: "Regular expression can't start and end with slashes",
        brackets: 'Regular expression must contain brackets',
        invalidBrackets: 'Invalid brackets',
        fullBgpLimitThresholdRange: `Threshold has to be between ${BGP_LIMIT_MIN} and ${BGP_LIMIT_MAX}`,
      },
    },
    jumphost: {
      boxTitle: 'Jumphost settings',
      btnAdd: 'Add',
      btnEdit: 'Edit',
      btnDelete: 'Delete',
      btnDownload: 'Download public key',
      btnUpdate: 'Update',
      btnClose: 'Close',
      yes: 'Yes',
      no: 'No',
      running: 'Running',
      inactive: 'Inactive',
      deleteJumphostConfirmDialog: {
        header: 'Jumphost will be deleted',
        headerMulti: 'Selected rows will be deleted',
        title: 'Delete jumphost?',
        sellectedJumphostsTitles: 'Delete selected jumphosts?',
        btnConfirm: 'Delete',
        btnCancel: 'Cancel',
      },
      columns: {
        enabled: { name: 'Enabled', help: '' },
        label: { name: 'Label', help: '' },
        address: { name: 'Jumphost Address', help: '' },
        loginType: { name: 'Login Type', help: '' },
        subnets: { name: 'Subnets', help: '' },
        exclude: { name: 'Exclude Subnets', help: '' },
        running: { name: 'Status', help: '' },
        username: { name: 'Username', help: '' },
        action: { name: 'Action', help: '' },
      },
      form: {
        addTitle: 'Add Jumphost',
        editTitle: 'Edit Jumphost {name}',
        enabled: 'Enabled',
        disabled: 'Disabled',
        label: 'Label',
        address: 'Jumphost Address',
        subnets: 'IPv4 subnets',
        subnetsTooltip:
          'If you use 0.0.0.0/0 or another subnet which includes your network from which you are connecting to IP Fabric, make sure you put your network to “Exclude IPv4 subnet”. Otherwise, your IP connection will be lost and you will have to recover from the console.',
        exclude: 'Exclude IPv4 subnets',
        subnetsPlaceholer: 'Add CIDR',
        loginType: 'Login type',
        username: 'Username',
        password: 'Password',
        loginTypeOptions: {
          credentials: 'Use credentials',
          'ssh-key': 'Use SSH key',
        },
        validation: tagsInputIpFieldValidation,
      },
      request: {
        saveSuccess: 'Jumphost saved',
        updateSuccess: 'Jumphost updated',
        deleteJumphostSuccess: 'Jumphost deleted',
        deleteJumphostsSuccess: 'Jumphosts deleted',
        resourceConflict:
          "Can't save, the jumphost address {address} is already saved.",
      },
    },
    customPortSettings: {
      add: 'Add',
      edit: 'Edit',
      title: 'Custom SSH/Telnet ports',
      columns: {
        action: 'Action',
        label: 'Label',
        protocol: 'Protocol',
        port: 'Port',
        subnets: 'Subnets',
        excludeSubnets: 'Exclude Subnets',
      },
      form: {
        excludeSubnets: 'Exclude IPv4 subnets',
        label: 'Label',
        port: 'Port',
        protocol: 'Protocol',
        subnets: 'IPv4 subnets',
        validations: {
          ...tagsInputIpFieldValidation,
          required: 'This field is required',
          portRange: 'Port must be between 1 and 65535',
        },
        placeholders: {
          excludeSubnets: 'Add CIDR',
          subnets: 'Add CIDR',
        },
      },
      protocols: {
        ssh: 'SSH',
        telnet: 'Telnet',
      },
      dialogs: {
        add: {
          title: 'Add custom SSH/Telnet port',
          primaryAction: 'Add',
          close: 'Close',
        },
        edit: {
          title: 'Edit custom SSH/Telnet port',
          primaryAction: 'Update',
          close: 'Close',
        },
      },
      request: {
        saveSuccess: 'Port setting saved',
        updateSuccess: 'Port setting updated',
        deleteSettingsSuccess: 'Port settings deleted',
      },
    },
    cliSettings: {
      allowTelnetDescription: 'Allow using telnet protocol',
      boxTitle: 'CLI Settings',
      retryDefault: 'Basic failure',
      retryDefaultDescription:
        'How many times to retry a connection for any error, except authentication failure.',
      retryAuth: 'Authentication failure',
      retryAuthDescription:
        'How many times to retry a connection when it fails on authentication.',
      readyTimeout: 'Network device login timeout',
      readyTimeoutDescription: 'How many seconds to wait for initial login',
      cmdTimeout: 'Network device session timeout',
      cmdTimeoutDescription:
        'How many seconds to wait after the last data were received',
      unauthenticatedTimeout: 'Network device authentication timeout',
      unauthenticatedTimeoutDescription:
        'How many seconds to wait for the login prompt to appear',
      commandResponseTimeoutMinutes: 'Command response timeout',
      commandResponseTimeoutMinutesDescription:
        'How many minutes to wait for a device to finish sending the response to a command',
      cliLimitDescription: 'Maximum number of parallel sessions',
      seconds: 'seconds',
      miliseconds: 'ms',
      minutes: 'minutes',
      retries: 'retries',
      btnSave: 'Save',
      tacacs: 'Command Authorization Failure retries',
      tacacsDescription:
        'How many times try to send the same command after authorization failure, and the delay before the next attempt.',
      validation: {
        required: 'Required',
        number: 'Must be a number',
        rangeReadyTimeout: `Timeout must be between ${loginTimeouts.min} to ${loginTimeouts.max} seconds`,
        rangeCmdTimeout: `Timeout must be between ${sessionTimeouts.min} to ${sessionTimeouts.max} seconds`,
        rangeUnauthenticatedTimeout: `Timeout must be between ${authenticationTimeouts.min} to ${authenticationTimeouts.max} seconds`,
        rangeCommandResponseTimeoutMinutes: `Timeout must be between ${commandTimeoutsMinutes.min} to ${commandTimeoutsMinutes.max} minutes`,
        tacacsDelayRange: `Delay must be between ${tacacsDelay.min} to ${tacacsDelay.max} ms`,
        tacacsRetryRange: `Value must be between ${tacacsRetry.min} to ${tacacsRetry.max}`,
        tacacsDelayCmdTimeout:
          "Tacacs delay can't be greater than network device session timeout",
        cliLimit: `Value must be greater or equal to ${cliLimitSessions.min}`,
        retryDefault: `Value must be between ${retryDefault.min} to ${retryDefault.max}`,
        retryAuth: `Value must be between ${retryAuth.min} to ${retryAuth.max}`,
      },
    },
    vendorsApi: {
      btnAdd: 'Add',
      btnSave: 'Save',
      btnUpdate: 'Update',
      btnDelete: 'Delete',
      btnEdit: 'Edit',
      btnUseGlobalSettings: 'Override with global settings',
      bulkActions: {
        enable: 'Enable',
        disable: 'Disable',
        delete: 'Delete',
      },
      help: (
        <p>
          For network devices that require discovery via a controller or central
          management tool, define the API endpoint details.
        </p>
      ),
      noItems: {
        heading: 'Setup Vendors API Connection',
        addItemsText: (
          <span>
            Click on + to add a new vendor API connection
            <br />
            which will be used in discovery
          </span>
        ),
      },
      deleteVendorDialog: {
        header: 'Vendor will be deleted',
        headerMultiple: '{count} vendors will be deleted',
        subHeader: 'Do you really want to delete {name}?',
        subHeaderMultiple: 'Do you really want to delete {count} entries?',
        btnConfirm: 'Delete',
        btnSecondaryConfirm: 'Cancel',
      },
      useGlobalSettingsConfirmDialog: {
        header: 'Update Vendor API settings from global settings',
        subHeader: 'Overwrite current settings?',
        btnConfirm: 'Overwrite',
        btnSecondaryConfirm: 'Cancel',
      },
      vendorsEmpty: 'Vendors list must not be empty',
      table: {
        title: 'Vendors API',
        columns: {
          isEnabled: 'Enable',
          type: 'Type',
          baseUrl: 'Base URL',
          details: 'Details',
          actions: 'Actions',
          slug: 'Slug',
          comment: 'Comment',
        },
      },
      vendorTypes: {
        [settingTypes.vendorApiNames.aws]: 'AWS',
        [settingTypes.vendorApiNames.azure]: 'Azure',
        [settingTypes.vendorApiNames.checkpoint]: 'CheckPoint Management',
        [settingTypes.vendorApiNames.ciscoapic]: 'Cisco APIC',
        [settingTypes.vendorApiNames.ciscofmc]: 'Cisco FMC',
        [settingTypes.vendorApiNames.f5osa]: 'F5OS-A (R)',
        [settingTypes.vendorApiNames.f5osc]: 'F5OS-C (Velos)',
        [settingTypes.vendorApiNames.junipermist]: 'Juniper Mist',
        [settingTypes.vendorApiNames.meraki]: 'Meraki REST',
        [settingTypes.vendorApiNames.nsxT]: 'VMware NSX-T',
        [settingTypes.vendorApiNames.ruckusvsz]: 'Ruckus Virtual SmartZone',
        [settingTypes.vendorApiNames.silverpeak]: 'Silver Peak',
        [settingTypes.vendorApiNames.versa]: 'Versa',
        [settingTypes.vendorApiNames.viptela]: 'Viptela',
        [settingTypes.vendorApiNames.prisma]: 'Prisma',
        [settingTypes.vendorApiNames.forcepoint]: 'Forcepoint',
        [settingTypes.vendorApiNames.gcp]: 'Google Cloud Platform',
        [settingTypes.vendorApiNames.velocloud]: 'VeloCloud',
        [settingTypes.vendorApiNames.arubaNewCentral]: 'Aruba New Central',
      },
      unknownVendor: 'Unknown vendor',
      vendorDialog: {
        type: 'Type',
        btnClose: 'Close',
        btnTest: 'Test connection',
        addTitle: 'Add Connection',
        editTitle: 'Edit {name}',
        vendor: 'vendor',
      },
      forcepointForm: {
        authenticationKey: '* Authentication Key',
      },
      merakiForm: {
        apiKey: '* API Key',
        organizations: 'Organizations IDs',
        organizationsHelp: (
          <div>
            If you don't specify it then all organizations will be processed.
          </div>
        ),
        apiVer: '* API Version',
        validation: {
          required: 'Required',
          httpsRequired: 'URL has to start with https://',
          invalidIp: "{ip} isn't valid entry",
          invalidNet:
            '{net} is too large. Prefix length has to be higher than /{mask}',
        },
      },
      prismaForm: {
        tsgid: '* TSG ID',
      },
      arubaNewCentralForm: {
        loginType: '* Auth Mode',
        loginTypeOptions: {
          central: 'Central',
          glp: 'GreenLake (GLP)',
          msp: 'MSP',
        },
        tenantId: '* Workspace ID',
        clientId: '* Client ID',
        clientSecret: '* Client Secret',
      },
      versaForm: {
        paginationLimit: 'Pagination Limit',
        paginationLimitTooltip:
          'If the pagination limit is not set, the pagination limit would be set 1000 by default.',
      },
      silverpeak: {
        loginType: 'Login Type',
      },
      checkpointApiForm: {
        versionLabel:
          'Management server version R80.20.M1 and above is supported',
        isEnabled: 'Enable',
        apiKey: '* API Key',
        apiKeyHelp: 'Available from version R80.40',
        domains: 'Collect following domains',
        domainsDescription:
          'Mandatory if Base URL points to Multi-Domain Server',
        domainsHelp: (
          <span>
            Version: &gt;= v1.2 (
            <a
              href='https://sc1.checkpoint.com/documents/latest/APIs/#api_versions~v1.2%20'
              target='_blank'
              rel='noreferrer'
            >
              https://sc1.checkpoint.com/documents/latest/APIs/#api_versions~v1.2%20
            </a>
            )
          </span>
        ),
        validation: {
          required: 'Field is required',
          portRange: 'Port has to be 1-65535',
          baseUrlStartsWith: 'Server has to start with http(s)://',
          oneOfRequired: 'API Key or Username and Password is required',
        },
      },
      ciscoFmcForm: {
        token: '* Bearer token',
        tokenTooltip: 'Token is used for CDO FMC only',
        authentication: '* Authentication',
        validation: {
          required: 'Field is required',
          oneOfRequired: 'API Key or Username and Password is required',
          baseUrlStartsWith: 'Server has to start with http(s)://',
        },
      },
      awsForm: {
        isEnabled: 'Enable',
        apiKey: '* Access Key ID',
        apiSecret: '* Secret Access Key',
        assumeRoles: 'Assume Roles',
        assumeRolesTooltip: 'You can enter multiple ARNs, each on a new line.',
        regions: '* Regions',
        validation: {
          duplicateAssumeRoles: 'This field cannot contain duplicate rows',
          required: 'Field is required',
        },
      },
      gcpForm: {
        credentialsJson: '*GCP private key',
        credentialsJsonTooltip:
          'JSON Private key file generated from GCP containing all necessary configs',
        confirmDialogHeaderMsg: 'The GCP private key has already been uploaded',
      },
      veloCloudApiForm: {
        isEnabled: 'Enable',
        authentication: '* Authentication',
        apiToken: '* API Token',
        validation: {
          required: 'Field is required',
          baseUrlStartsWith: 'Server has to start with http(s)://',
        },
      },
      labels: {
        apiToken: '* API Token',
        apiVersion: '* API Version',
        baseUrl: '* Base URL',
        enabled: {
          on: 'Enabled',
          off: 'Disabled',
        },
        rejectUnauthorized: 'Reject unauthorized certificates',
        respectSystemProxyConfiguration: 'Use system proxy configuration',
        combinedDiscovery: 'Allow API+CLI combined discovery',
        password: '* Password',
        username: '* Username',
        slug: '* Slug',
        slugTooltip:
          'Slug is a unique descriptive identifier of the configuration instance. Use short descriptive value, like aws-eu.',
        comment: 'Comment',
        maxConcurrentRequests: 'Maximum concurrent requests',
        maxCapacity: 'maxCapacity',
        refillRate: 'refillRate',
      },
      validations: {
        baseUrlStartsWith: 'Server has to start with http(s)://',
        required: 'Field is required',
        pattern: 'Field fails to match the required pattern: {pattern}',
      },
      azure: {
        isEnabled: 'Enable',
        subscriptionIds: 'Subscription IDs',
        subscriptionIdsTooltip:
          'You can enter multiple subscription IDs, each on a new line. Leave empty to process all subscriptions.',
        tenantId: '* Tenant ID',
        clientId: '* Client ID',
        clientSecret: '* Client Secret',
        validation: {
          required: 'Field is required',
        },
      },
      request: {
        deleteSuccess: 'Item deleted',
        saveSuccess: 'Item saved',
        updateSuccess: 'Item updated',
        vendorDisabledSuccess: 'Vendor disabled',
        vendorEnabledSuccess: 'Vendor enabled',
      },
      rateLimitingSettings: {
        requests: 'requests',
        requestsPer: 'requests per',
        milliseconds: 'ms',
        hideSettings: 'Hide advanced settings',
        showSettings: 'Show advanced settings',
      },
      noVendorApis: 'No stored vendor APIs',
    },
    assuranceEngine: assuranceEngineMessages.en,
  },
};
